import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ListItemIcon } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import IconButton from '@mui/joy/IconButton';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import Tooltip from '@mui/joy/Tooltip';
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Box,
  Typography,
  Divider,
  Button,
} from '@mui/joy';
import HeaderOffset from './HeaderOffset';
import ColorSchemeToggle from './ColorSchemeToggle';
import { useSidebar } from '../hooks/useSidebar';
import { logout } from '../redux/authReducer';
import packageJson from '../../package.json';
import {
  Drawer,
  dividerStyles,
  drawerItems,
  getDrawerItemId,
  getAnimatedSidebarGroupStyles,
  getAnimatedSidebarTextStyles,
  footerColorToggleStyles,
  getFooterContentStyles,
  getFooterControlRowStyles,
  getFooterStyles,
  getNavigationItemIconSlotStyles,
  getNavigationItemStyles,
  navigationItemIconStyles,
  getProfileButtonStyles,
  getProfileSectionStyles,
  logoButtonStyles,
  logoutButtonStyles,
  toggleButtonStyles,
  versionBadgeStyles,
} from './sidebarStyles';

const { version } = packageJson;

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [open, setOpen] = useSidebar();
  const [isDialogOpen, setDialog] = useState(false);
  const { user } = useSelector((state) => state.auth);

  const isProfileSelected = location.pathname.includes('/app/profile');
  const username = user?.username ?? 'Profile';
  const userInitial = username.charAt(0).toUpperCase();

  const handleNavigate = (path) => {
    if (path.startsWith('http')) {
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(path);
  };

  const isItemSelected = (item) => {
    if (location.pathname === item.path) {
      return true;
    }

    if (item.path !== '/app/tasks' && location.pathname.startsWith(item.path)) {
      return true;
    }

    return false;
  };

  const toggleDialog = () => {
    setDialog((previous) => !previous);
  };

  const onExit = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <>
      <Drawer
        variant="permanent"
        open={open}
        data-testid="sidebar-drawer"
        PaperProps={{ 'data-testid': 'sidebar-drawer-paper' }}
      >
        <HeaderOffset
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: open ? 'space-between' : 'center',
            gap: open ? 1 : 0,
            px: open ? 1.5 : 0,
          }}
        >
          <Box
            data-testid="sidebar-brand-content"
            aria-hidden={!open}
            sx={[getAnimatedSidebarGroupStyles(open, 132), !open && { display: 'none' }]}
          >
            <IconButton
              aria-label="Go to agents"
              variant="soft"
              size="sm"
              onClick={() => handleNavigate('/app/agents')}
              sx={logoButtonStyles}
              disabled={!open}
              tabIndex={open ? 0 : -1}
            >
              <Box
                component="img"
                src="/logo_full.png"
                alt="DCS logo"
                sx={{ width: 20 }}
              />
            </IconButton>
            <Typography level="title-lg" noWrap sx={{ color: 'text.primary' }}>
              DCS
            </Typography>
          </Box>
          <IconButton
            aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
            onClick={() => setOpen((previous) => !previous)}
            sx={toggleButtonStyles}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
        </HeaderOffset>

        <Divider sx={dividerStyles} />

        <List
          sx={{
            flex: 1,
            px: open ? 1 : 0,
            py: 1.5,
            justifyContent: 'flex-start',
            overflowY: 'auto',
            '--ListItem-radius': (theme) => theme.vars.radius.md,
            ...(!open && {
              '--ListItem-paddingLeft': '0px',
              '--ListItem-paddingRight': '0px',
            }),
          }}
        >
          {drawerItems.map((item) => {
            const isSelected = isItemSelected(item);
            const drawerItemId = getDrawerItemId(item.label);
            const NavigationIcon = isSelected ? item.selectedIcon ?? item.icon : item.icon;
            const navigationButton = (
              <ListItemButton
                aria-current={isSelected ? 'page' : undefined}
                aria-label={item.label}
                data-testid={`sidebar-nav-button-${drawerItemId}`}
                onClick={() => handleNavigate(item.path)}
                selected={isSelected}
                sx={getNavigationItemStyles(open)}
              >
                <ListItemIcon
                  data-testid={`sidebar-nav-icon-slot-${drawerItemId}`}
                  sx={getNavigationItemIconSlotStyles()}
                >
                  <NavigationIcon
                    data-testid={`sidebar-nav-icon-${drawerItemId}`}
                    fontSize="inherit"
                    sx={navigationItemIconStyles}
                  />
                </ListItemIcon>
                <ListItemContent
                  data-testid={`sidebar-nav-content-${drawerItemId}`}
                  aria-hidden={!open}
                  sx={[
                    { flex: open ? '1 1 auto' : '0 0 auto' },
                    getAnimatedSidebarTextStyles(open, 116),
                    !open && { display: 'none' },
                  ]}
                >
                  <Typography level="title-sm" noWrap>
                    {item.label}
                  </Typography>
                </ListItemContent>
              </ListItemButton>
            );

            return (
              <ListItem key={item.label} sx={{ display: 'block', mb: 0.75 }}>
                {!open ? (
                  <Tooltip title={item.label} variant="solid" placement="right">
                    {navigationButton}
                  </Tooltip>
                ) : navigationButton}
              </ListItem>
            );
          })}
        </List>

        <Divider sx={dividerStyles} />

        <Box sx={getFooterStyles()}>
          <Box sx={getFooterControlRowStyles(open)}>
            <ColorSchemeToggle
              tooltipPlacement={open ? 'top' : 'right'}
              sx={footerColorToggleStyles}
            />
          </Box>

          {(() => {
            const apiDocsPath = '/app/api-docs';
            const isApiDocsSelected = location.pathname.startsWith(apiDocsPath);
            const ApiDocsIcon = isApiDocsSelected ? DescriptionIcon : DescriptionOutlinedIcon;
            const btn = (
              <ListItemButton
                selected={isApiDocsSelected}
                onClick={() => handleNavigate(apiDocsPath)}
                sx={getNavigationItemStyles(open)}
              >
                <Box component="span" sx={getNavigationItemIconSlotStyles()}>
                  <ApiDocsIcon sx={navigationItemIconStyles} />
                </Box>
                {open && (
                  <ListItemContent>
                    <Typography level="body-sm">API Docs</Typography>
                  </ListItemContent>
                )}
              </ListItemButton>
            );
            return (
              <Box>
                {!open ? (
                  <Tooltip title="API Docs" variant="solid" placement="right">
                    {btn}
                  </Tooltip>
                ) : btn}
              </Box>
            );
          })()}

          <Box sx={getFooterContentStyles(open)}>
            <Box sx={getProfileSectionStyles(open)}>
              <IconButton
                aria-label="Open profile"
                onClick={() => handleNavigate('/app/profile')}
                size="sm"
                variant="plain"
                color="neutral"
                sx={getProfileButtonStyles(isProfileSelected)}
              >
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: isProfileSelected ? 'primary.500' : 'neutral.400',
                    color: 'common.white',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                  }}
                >
                  {userInitial}
                </Avatar>
              </IconButton>

              <Box
                data-testid="sidebar-account-content"
                aria-hidden={!open}
                sx={[
                  { flex: open ? '1 1 auto' : '0 0 auto' },
                  getAnimatedSidebarTextStyles(open, 120),
                  !open && { display: 'none' },
                ]}
              >
                <Typography level="title-sm" noWrap>
                  {username}
                </Typography>
                <Typography level="body-xs" noWrap sx={{ color: 'text.tertiary' }}>
                  Account
                </Typography>
              </Box>
            </Box>

            <IconButton
              aria-label="Log out"
              size="sm"
              variant="plain"
              color="neutral"
              onClick={toggleDialog}
              sx={logoutButtonStyles}
            >
              <LogoutRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          {open && (
            <Typography level="body-xs" sx={versionBadgeStyles}>
              {`v${version}${process.env.REACT_APP_GIT_COMMIT ? ` · ${process.env.REACT_APP_GIT_COMMIT}` : ''}`}
            </Typography>
          )}
        </Box>
      </Drawer>

      <Modal open={isDialogOpen} onClose={toggleDialog}>
        <ModalDialog>
          <DialogTitle>You want to leave so soon?</DialogTitle>
          <DialogContent>Are you sure you want to exit?</DialogContent>
          <DialogActions>
            <Button color="danger" onClick={onExit}>
              Exit
            </Button>
            <Button variant="plain" onClick={toggleDialog}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default Sidebar;
